import { useState, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { DocumentData } from 'firebase/firestore'
import { useAuth } from '@/hooks/useAuth'
import { useDocument } from '@/hooks/useFirestore'
import { useBusinesses, useUsers } from '@/hooks/useAdminData'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Toggle } from '@/components/ui/Toggle'
import { Badge } from '@/components/ui/Badge'
import { useTranslation } from 'react-i18next'
import { enumLabel } from '@/lib/enumLabel'
import {
  enabledKeys,
  featureCollection,
  saveFeatureList,
  savedFeatureList,
  type FeatureScope,
} from '@/lib/featureSettings'
import { APP_FEATURE_KEYS, type AppFeatureKey } from '@/types'

/** Unsaved toggle changes, tied to the circle/user they were made for. */
interface Draft {
  target: string
  enabled: AppFeatureKey[]
}

const sameKeys = (a: AppFeatureKey[], b: AppFeatureKey[]) =>
  a.length === b.length && a.every((key) => b.includes(key))

export function FeaturesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<FeatureScope>('global')
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('')
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)

  const { userProfile } = useAuth()
  const { data: businesses } = useBusinesses()
  const { data: users } = useUsers()

  const targetId = activeTab === 'global' ? selectedBusinessId : selectedUserId
  const collectionName = featureCollection(activeTab)
  const { data: settingsDoc, isLoading: settingsLoading } = useDocument<DocumentData>(
    collectionName,
    targetId || undefined
  )

  const savedList = useMemo(() => savedFeatureList(activeTab, settingsDoc), [activeTab, settingsDoc])
  const baseline = useMemo(() => enabledKeys(savedList), [savedList])
  const draftKey = `${activeTab}:${targetId}`
  const localEnabled = draft?.target === draftKey ? draft.enabled : baseline
  const hasChanges = !sameKeys(localEnabled, baseline)

  const select = (tab: FeatureScope, id: string) => {
    setActiveTab(tab)
    if (tab === 'global') setSelectedBusinessId(id)
    else setSelectedUserId(id)
    setDraft(null)
    setSaved(false)
  }

  const toggleFeature = (key: AppFeatureKey) => {
    const next = localEnabled.includes(key)
      ? localEnabled.filter((k) => k !== key)
      : [...localEnabled, key]
    setDraft({ target: draftKey, enabled: next })
    setSaved(false)
  }

  const handleSave = async () => {
    if (!targetId) return
    setSaving(true)
    try {
      await saveFeatureList(activeTab, targetId, localEnabled, savedList, userProfile?.id ?? '')
      // Re-read the saved document so it becomes the new baseline: Save disables and
      // Reset returns to what was just saved, not to the state before it.
      await queryClient.invalidateQueries({ queryKey: [collectionName, targetId] })
      setDraft(null)
      setSaved(true)
    } catch {
      alert(t('features.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const nothingSelected = !targetId

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeTab === 'global' ? 'primary' : 'secondary'}
          onClick={() => select('global', selectedBusinessId)}
        >
          {t('features.globalTab')}
        </Button>
        <Button
          variant={activeTab === 'user' ? 'primary' : 'secondary'}
          onClick={() => select('user', selectedUserId)}
        >
          {t('features.userTab')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4">
            {activeTab === 'global' ? (
              <div className="w-full space-y-1 sm:w-auto">
                <label htmlFor="feature-business" className="text-sm font-medium text-gray-700">{t('features.selectBusiness')}</label>
                <select
                  id="feature-business"
                  value={selectedBusinessId}
                  onChange={(e) => select('global', e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm sm:w-64"
                >
                  <option value="">{t('features.chooseBusiness')}</option>
                  {businesses?.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="w-full space-y-1 sm:w-auto">
                <label htmlFor="feature-user" className="text-sm font-medium text-gray-700">{t('features.selectUser')}</label>
                <select
                  id="feature-user"
                  value={selectedUserId}
                  onChange={(e) => select('user', e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm sm:w-64"
                >
                  <option value="">{t('features.chooseUser')}</option>
                  {users?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {nothingSelected ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {activeTab === 'global'
                ? t('features.selectBusinessPrompt')
                : t('features.selectUserPrompt')}
            </p>
          ) : settingsLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">{t('common.loading')}</p>
          ) : (
            <div className="space-y-4">
              {savedList === null && (
                <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
                  {t(activeTab === 'global' ? 'features.noSettingsGlobal' : 'features.noSettingsUser')}
                </div>
              )}
              {activeTab === 'user' && (
                <p className="text-xs text-gray-500">{t('features.userScopeHint')}</p>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                {APP_FEATURE_KEYS.map((key) => {
                  const enabled = localEnabled.includes(key)
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between rounded-lg border border-gray-200 p-3"
                    >
                      <div className="flex items-center gap-3">
                        <Toggle checked={enabled} onChange={() => toggleFeature(key)} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {enumLabel(t, 'featureNames', key)}
                          </p>
                          <div className="flex gap-1 mt-0.5">
                            <Badge variant="info">iOS</Badge>
                            <Badge variant="default">Android</Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4">
                {saved && !hasChanges && (
                  <span className="mr-auto text-sm text-green-700">{t('features.saved')}</span>
                )}
                <Button
                  variant="secondary"
                  onClick={() => setDraft(null)}
                  disabled={!hasChanges || saving}
                >
                  {t('common.reset')}
                </Button>
                <Button onClick={handleSave} disabled={!hasChanges || saving}>
                  {saving ? t('common.saving') : t('features.save')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
